/**
 * RBAC + Feedback + Audit API — Phase-2 frontend client.
 *
 * Wraps the backend `/api/v2/workflow/*` endpoints from Phase 1:
 *   - RBAC manifest (per-user page permissions)
 *   - Page-level feedback capture + aggregate
 *   - User-journey tracking + session replay
 *   - Prediction → action → outcome audit trail
 *   - Nightly feedback-learner run history
 *
 * File named `rbacApi.ts` (not `workflowApi.ts`) because the existing
 * `services/workflowApi.ts` covers maintenance/inspection workflows —
 * different domain, kept separate to avoid regressions.
 */

import api from './api'

// ─── Types ───────────────────────────────────────────────────────────

export type RoleName =
  | 'viewer' | 'operator' | 'technician' | 'engineer'
  | 'supervisor' | 'manager' | 'sme' | 'executive' | 'admin'

export interface PageManifest {
  page_id: string
  title: string
  category: string
  route: string
  view_roles: RoleName[]
  write_roles: RoleName[]
  feedback_enabled: boolean
  spoke_enabled: boolean
  description?: string
  can_write_for_current_user?: boolean | null
}

export interface RbacManifest {
  role: RoleName | null
  spoke: boolean
  pages: PageManifest[]
  total_pages: number
  categories: string[]
}

export interface FeedbackCaptureIn {
  page_id: string
  rating: -1 | 0 | 1
  prediction_id?: string
  asset_id?: string
  industry_id?: string
  comment?: string
  expected_output?: string
  prompt?: string
  actual_output?: string
  context?: Record<string, unknown>
}

export interface FeedbackCaptureOut {
  feedback_id: string
  weight: number
  created_at: string
}

export interface FeedbackAggregateRow {
  page_id: string
  user_role: string
  n: number
  up: number
  down: number
  consumed: number
  with_expected: number
}

export interface FeedbackAggregate {
  since_hours: number
  filters: Record<string, unknown>
  rows: FeedbackAggregateRow[]
  total_feedback_events: number
}

export interface JourneyEvent {
  event_id: string
  user_id?: string
  user_role?: string
  page_id: string
  action: string
  asset_id?: string
  context?: Record<string, unknown>
  occurred_at: string
}

export interface PredictionTrailFeedback {
  feedback_id: string
  user_role: string
  rating: number
  comment?: string
  expected_output?: string
  created_at: string
}

export interface PredictionTrailOutcome {
  outcome_id: string
  recommended_action?: string
  action_taken?: string
  action_taken_at?: string
  outcome_correct?: boolean | null
  outcome_observed_at?: string
  outcome_notes?: string
}

export interface PredictionTrail {
  prediction_id: string
  feedback: PredictionTrailFeedback[]
  outcomes: PredictionTrailOutcome[]
}

export interface LearningRun {
  run_id: string
  started_at: string
  finished_at?: string
  feedback_items_consumed: number
  preference_pairs_built: number
  base_model_version?: string
  validation_score?: number | null
  baseline_score?: number | null
  decision: string
  decision_reason?: string
  regression_flags?: string[]
}

// ─── RBAC ───────────────────────────────────────────────────────────

const WF = '/workflow'

export async function getRbacManifest(): Promise<RbacManifest> {
  const { data } = await api.get<RbacManifest>(`${WF}/rbac/manifest`)
  return data
}

export async function listAllowedPages(): Promise<{
  role: RoleName
  pages: Array<{ page_id: string; title: string; category: string; route: string; can_write: boolean }>
}> {
  const { data } = await api.get(`${WF}/rbac/pages`)
  return data
}

// ─── Feedback ───────────────────────────────────────────────────────

export async function captureFeedback(payload: FeedbackCaptureIn): Promise<FeedbackCaptureOut> {
  const { data } = await api.post<FeedbackCaptureOut>(`${WF}/feedback/capture`, payload)
  return data
}

export async function getFeedbackAggregate(filters: {
  page_id?: string; role?: string; asset_id?: string; since_hours?: number
} = {}): Promise<FeedbackAggregate> {
  const { data } = await api.get(`${WF}/feedback/aggregate`, { params: filters })
  return data
}

// ─── Journey ────────────────────────────────────────────────────────

export interface JourneyIn {
  page_id: string
  action: string
  session_id?: string
  asset_id?: string
  context?: Record<string, unknown>
}

export async function trackJourney(payload: JourneyIn): Promise<{ event_id: string } | null> {
  // Best-effort: tracking must never block the UI. Swallow errors.
  try {
    const { data } = await api.post(`${WF}/user/track`, payload)
    return data
  } catch {
    return null
  }
}

export async function getSessionJourney(sessionId: string): Promise<{
  session_id: string; events: JourneyEvent[]
}> {
  const { data } = await api.get(`${WF}/user/journey/${encodeURIComponent(sessionId)}`)
  return data
}

// ─── Audit: prediction → action → outcome ───────────────────────────

export async function recordAction(payload: {
  prediction_id: string
  action_taken: string
  asset_id?: string
  recommended_action?: string
  context?: Record<string, unknown>
}): Promise<{ outcome_id: string }> {
  const { data } = await api.post(`${WF}/audit/action-taken`, payload)
  return data
}

export async function confirmOutcome(payload: {
  prediction_id: string
  outcome_correct: boolean
  outcome_notes?: string
}): Promise<{ outcome_id: string; confirmed: boolean }> {
  const { data } = await api.post(`${WF}/audit/confirm-outcome`, payload)
  return data
}

export async function getPredictionTrail(predictionId: string): Promise<PredictionTrail> {
  const { data } = await api.get(`${WF}/audit/prediction-trail/${encodeURIComponent(predictionId)}`)
  return data
}

export async function getLearningRuns(limit = 30): Promise<{ runs: LearningRun[] }> {
  const { data } = await api.get(`${WF}/audit/learning-runs`, { params: { limit } })
  return data
}
